// Client instructions generated from an McpAccessProfile. Pure: no React, no
// network. The generator never receives a credential — every snippet carries
// a placeholder built from the provider's key prefix, so nothing here can
// print a real key.
import { CHATGPT_ADD_SERVER_STEPS } from './links';
import type {
  McpAccessProfile,
  McpApiKeySpec,
  McpAuthMode,
  McpClientId,
  McpClientSetup,
  McpSnippet,
  McpTroubleshootingItem,
  CredentialOwnership,
} from './types';

export const KEY_PLACEHOLDER_SUFFIX = 'YOUR_KEY_HERE';

export const CLIENTS: McpClientId[] = ['chatgpt', 'claude', 'cursor', 'other'];

/** What each client can do with a remote MCP server today. */
export const CLIENT_SUPPORT: Record<
  McpClientId,
  { label: string; oauth: boolean; apiKey: boolean }
> = {
  chatgpt: { label: 'ChatGPT', oauth: true, apiKey: false },
  claude: { label: 'Claude', oauth: true, apiKey: false },
  cursor: { label: 'Cursor', oauth: true, apiKey: true },
  other: { label: 'Other clients', oauth: true, apiKey: true },
};

const METHOD_LABELS: Record<McpAuthMode, string> = {
  oauth: 'One-click sign-in (OAuth)',
  api_key: 'Personal API key in a header',
  none: 'No authentication',
  custom: 'Custom authentication',
};

export const keyPlaceholder = (profile: McpAccessProfile): string =>
  `${profile.apiKey?.prefix ?? ''}${KEY_PLACEHOLDER_SUFFIX}`;

export const headerValuePlaceholder = (profile: McpAccessProfile): string =>
  profile.apiKey?.scheme
    ? `${profile.apiKey.scheme} ${keyPlaceholder(profile)}`
    : keyPlaceholder(profile);

export const configNameFor = (profile: McpAccessProfile) =>
  profile.id.toLowerCase().replace(/[^a-z0-9-]+/g, '-');

const slugOf = configNameFor;

export const stripEmphasis = (text: string) => text.replace(/\*\*/g, '');

/* Where a person keeps a key on their own machine, and the variable that
 * client configs read it from, so no config file or link ever holds it. */
export const keyFilePath = (profile: McpAccessProfile) =>
  `~/.capitol/${slugOf(profile)}.key`;

export const keyEnvVar = (profile: McpAccessProfile) =>
  `${slugOf(profile).toUpperCase().replace(/-/g, '_')}_MCP_KEY`;

export const envHeaderValue = (spec: McpApiKeySpec, envVar: string) =>
  spec.scheme ? `${spec.scheme} \${env:${envVar}}` : `\${env:${envVar}}`;

export const saveKeyScript = (keyFile: string, envVar?: string) => {
  const dir = keyFile.slice(0, keyFile.lastIndexOf('/'));
  return [
    `mkdir -p ${dir} && chmod 700 ${dir}`,
    `pbpaste > ${keyFile} && chmod 600 ${keyFile}`,
    ...(envVar
      ? [`echo 'export ${envVar}="$(cat ${keyFile})"' >> ~/.zshrc`]
      : []),
  ].join('\n');
};

/** The method a client should use, or null when no valid one exists. */
export const chooseMethod = (
  profile: McpAccessProfile,
  client: McpClientId,
): McpAuthMode | null => {
  if (!profile.serverUrl) return null;
  const modes = new Set(profile.authModes);
  const support = CLIENT_SUPPORT[client];
  const can = (mode: McpAuthMode) =>
    modes.has(mode) &&
    (mode === 'none' ||
      (mode === 'oauth' && support.oauth) ||
      (mode === 'api_key' && support.apiKey && Boolean(profile.apiKey)));
  if (modes.has('none')) return 'none';
  const preferred = profile.clientPreferences?.[client];
  if (preferred && can(preferred)) return preferred;
  if (can('oauth')) return 'oauth';
  if (can('api_key')) return 'api_key';
  return null;
};

const json = (value: unknown) => JSON.stringify(value, null, 2);

export const snippets = {
  serverUrl: (profile: McpAccessProfile): McpSnippet => ({
    id: 'server_url',
    label: 'Server URL',
    text: profile.serverUrl ?? '',
    language: 'text',
  }),
  authHeader: (profile: McpAccessProfile): McpSnippet => ({
    id: 'auth_header',
    label: `${profile.apiKey?.headerName ?? 'Authorization'} header`,
    text: `${profile.apiKey?.headerName ?? 'Authorization'}: ${headerValuePlaceholder(profile)}`,
    language: 'text',
  }),
  headerValue: (profile: McpAccessProfile): McpSnippet => ({
    id: 'header_value',
    label: 'Header value',
    text: headerValuePlaceholder(profile),
    language: 'text',
  }),
  cursorConfig: (profile: McpAccessProfile, withKey: boolean): McpSnippet => ({
    id: 'cursor_config',
    label: 'Cursor config (mcp.json)',
    text: json({
      mcpServers: {
        [slugOf(profile)]:
          withKey && profile.apiKey
            ? {
                url: profile.serverUrl,
                headers: {
                  [profile.apiKey.headerName]: envHeaderValue(
                    profile.apiKey,
                    keyEnvVar(profile),
                  ),
                },
              }
            : { url: profile.serverUrl },
      },
    }),
    language: 'json',
  }),
  saveKey: (profile: McpAccessProfile): McpSnippet => ({
    id: 'save_key',
    label: 'Save the key',
    text: saveKeyScript(keyFilePath(profile), keyEnvVar(profile)),
    language: 'shell',
  }),
  claudeCode: (profile: McpAccessProfile): McpSnippet => ({
    id: 'claude_code',
    label: 'Claude Code command',
    text: `claude mcp add --transport http ${slugOf(profile)} ${profile.serverUrl}`,
    language: 'shell',
  }),
  mcpRemote: (profile: McpAccessProfile): McpSnippet => ({
    id: 'mcp_remote',
    label: 'Bridge for stdio-only clients (mcp-remote)',
    text: json({
      mcpServers: {
        [slugOf(profile)]: {
          command: 'npx',
          args: ['-y', 'mcp-remote', profile.serverUrl],
        },
      },
    }),
    language: 'json',
  }),
};

const createKeyStep = (profile: McpAccessProfile) => {
  const spec = profile.apiKey;
  if (!spec) return '';
  const where = spec.settingsLabel
    ? ` in ${profile.name} → ${spec.settingsLabel}`
    : '';
  const prefix = spec.prefix ? ` It starts with ${spec.prefix}.` : '';
  return `Create a ${spec.keyName}${where}.${prefix} Keys are personal — never share one or paste it into a chat.`;
};

const unsupportedReason = (
  profile: McpAccessProfile,
  client: McpClientId,
): string => {
  const label = CLIENT_SUPPORT[client].label;
  if (!profile.serverUrl)
    return `${profile.name} has no public MCP server URL, so other clients can't connect to it directly.`;
  if (profile.authModes.length === 1 && profile.authModes[0] === 'custom')
    return `${profile.name} uses its own authentication scheme, so there are no generic steps — see ${profile.name}'s documentation.`;
  if (profile.authModes.includes('api_key') && !CLIENT_SUPPORT[client].apiKey)
    return `${label} connects to MCP servers by signing in (OAuth), and ${profile.name} only accepts an API key. Use Cursor or another client that can send headers.`;
  return `${label} can't use any of the methods ${profile.name} supports.`;
};

const successLine = (profile: McpAccessProfile, client: McpClientId) =>
  `${CLIENT_SUPPORT[client].label} lists ${profile.name}'s tools, and the test prompt answers from your ${profile.name} data rather than guessing.`;

export const clientSetup = (
  profile: McpAccessProfile,
  client: McpClientId,
): McpClientSetup => {
  const method = chooseMethod(profile, client);
  const label = CLIENT_SUPPORT[client].label;
  const base = {
    client,
    label,
    method,
    methodLabel: method ? METHOD_LABELS[method] : 'Not available',
    note: profile.clientNotes?.[client],
    testPrompt:
      profile.examplePrompts[0] ?? `What can ${profile.name} tell me?`,
    success: successLine(profile, client),
  };
  if (!method) {
    return {
      ...base,
      supported: false,
      steps: [],
      snippets: [],
      unsupportedReason: unsupportedReason(profile, client),
    };
  }
  const url = profile.serverUrl as string;
  const consent =
    method === 'oauth'
      ? [`Approve on ${profile.name}'s consent screen, signed in as yourself.`]
      : [];

  if (client === 'chatgpt') {
    return {
      ...base,
      supported: true,
      steps: [
        CHATGPT_ADD_SERVER_STEPS.open,
        CHATGPT_ADD_SERVER_STEPS.add,
        CHATGPT_ADD_SERVER_STEPS.nameAndUrl(profile.name),
        CHATGPT_ADD_SERVER_STEPS.create(method === 'oauth'),
      ]
        .map(stripEmphasis)
        .concat(consent),
      snippets: [snippets.serverUrl(profile)],
    };
  }
  if (client === 'claude') {
    return {
      ...base,
      supported: true,
      steps: [
        'Open Customize in the Claude menu.',
        `Find ${profile.name} in the connector list and press Connect — or add a custom web connector with the name "${profile.name}" and the URL ${url}.`,
        ...(method === 'oauth'
          ? [`Press Allow on ${profile.name}'s authorization screen.`]
          : []),
      ],
      snippets: [snippets.serverUrl(profile)],
      extra: {
        title: 'Claude Code',
        steps: [
          'Run the command below in a terminal.',
          ...(method === 'oauth'
            ? ['Start a Claude Code session and run /mcp once to sign in.']
            : []),
        ],
        snippets: [snippets.claudeCode(profile)],
      },
    };
  }
  if (client === 'cursor') {
    if (method === 'api_key') {
      return {
        ...base,
        supported: true,
        steps: [
          createKeyStep(profile),
          `Open ~/.cursor/mcp.json and paste the config below. It holds no key: Cursor reads it from ${keyEnvVar(profile)}.`,
          `Copy the key and run the script below in a terminal on your Mac. It saves the key in ${keyFilePath(profile)}, readable only by you, and exports it as ${keyEnvVar(profile)}.`,
          'Quit and reopen Cursor so it loads the server and the variable.',
        ],
        snippets: [
          snippets.cursorConfig(profile, true),
          snippets.saveKey(profile),
        ],
      };
    }
    return {
      ...base,
      supported: true,
      steps: [
        'Open ~/.cursor/mcp.json and paste the config below (no key needed).',
        method === 'oauth'
          ? `Restart Cursor, then sign in to ${profile.name} when Cursor asks.`
          : 'Restart Cursor so it loads the server.',
      ],
      snippets: [snippets.cursorConfig(profile, false)],
    };
  }
  // other
  if (method === 'api_key') {
    return {
      ...base,
      supported: true,
      steps: [
        createKeyStep(profile),
        `Give the client the server URL and the header ${profile.apiKey?.headerName ?? 'Authorization'}: ${headerValuePlaceholder(profile)} with your own key.`,
        'Use the Streamable HTTP transport at exactly that URL.',
      ],
      snippets: [
        snippets.serverUrl(profile),
        snippets.authHeader(profile),
        snippets.headerValue(profile),
      ],
    };
  }
  return {
    ...base,
    supported: true,
    steps: [
      `Give the client the server URL ${url} and the Streamable HTTP transport.`,
      ...(method === 'oauth'
        ? [
            `When it asks, sign in to ${profile.name}; the client discovers the authorization server from the URL.`,
          ]
        : []),
      'Clients that only speak stdio can bridge with mcp-remote (config below).',
    ],
    snippets: [snippets.serverUrl(profile), snippets.mcpRemote(profile)],
  };
};

export const clientSetups = (profile: McpAccessProfile): McpClientSetup[] =>
  CLIENTS.map((client) => clientSetup(profile, client));

export const OWNERSHIP_COPY: Record<CredentialOwnership, string> = {
  personal:
    'Personal: each member connects their own account. The connection acts only as that member, and nobody else in the org can see or use it.',
  organization:
    'Organization: one credential, configured by an org admin and shared by the org’s agents and workflows. Never put a personal key in an organization field.',
  none: 'No credential: the server needs no sign-in.',
};

export const troubleshootingFor = (
  profile: McpAccessProfile,
): McpTroubleshootingItem[] => {
  const items: McpTroubleshootingItem[] = [];
  const modes = new Set(profile.authModes);
  if (modes.has('api_key') && profile.apiKey) {
    items.push({
      id: 'no-key',
      title: "I don't have an API key",
      body: `${createKeyStep(profile)} If you lost one, create a new key and delete the old one.`,
    });
    items.push({
      id: 'bad-key',
      title: 'Invalid, expired or revoked key (401)',
      body: `The header must read exactly ${profile.apiKey.headerName}: ${headerValuePlaceholder(profile)} — the whole key, no quotes. If it still fails the key was revoked or replaced: create a new one and update the client.`,
    });
  }
  if (modes.has('oauth')) {
    items.push({
      id: 'oauth',
      title: "Sign-in didn't finish",
      body: `Start again from the client's own Connect button — consent links are single-use and expire. Approve as the right ${profile.name} account; cancelling connects nothing.`,
    });
  }
  if (profile.serverUrl) {
    items.push({
      id: 'url',
      title: "404 or can't connect",
      body: `Use the server URL exactly: ${profile.serverUrl}${profile.serverUrl.endsWith('/') ? ' — with the trailing slash; some clients don’t follow the redirect without it' : ''}. Use the Streamable HTTP transport.`,
    });
  }
  items.push({
    id: 'disconnect',
    title: 'Disconnect an app or rotate a credential',
    body:
      profile.capitol.ownership === 'organization'
        ? 'Revoke the key on this server in Capitol, then mint a new one for each client that still needs access.'
        : `Remove ${profile.name} from the client's connector settings (or delete its entry from the config). To rotate a key, create a new one, update each client, then delete the old one in ${profile.name}.`,
  });
  if (modes.has('api_key')) {
    items.push({
      id: 'secrets',
      title: 'Never paste a key into chat or share it',
      body: `A key opens your ${profile.name} data to whoever holds it. Each person connects their own; if a key was exposed, delete it in ${profile.name} and create a new one.`,
    });
  }
  const byId = new Map(items.map((item) => [item.id, item]));
  for (const item of profile.troubleshooting ?? []) byId.set(item.id, item);
  return [...byId.values()];
};
